import React, { useState, useEffect } from 'react';
import { drinkAPI } from '../../utils/api';
import ImageUpload from '../common/ImageUpload';
import useSwipeToClose from '../../hooks/useSwipeToClose';
import './DrinkDetailModal.css';

const DrinkDetailModal = ({ drink, isOpen, onClose, onDrinkUpdated, user }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editedDrink, setEditedDrink] = useState(drink);
  const [showFullDescription, setShowFullDescription] = useState(false);

  // Add swipe to close functionality
  const swipeRef = useSwipeToClose(onClose, isOpen);

  useEffect(() => {
    setEditedDrink(drink);
    setIsEditing(false);
    setError('');
    setShowFullDescription(false);
  }, [drink]);

  const canEdit = user && (drink.user_id === user.id || drink.user_id === null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditedDrink(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...editedDrink.ingredients];
    newIngredients[index][field] = value;
    setEditedDrink(prev => ({
      ...prev,
      ingredients: newIngredients
    }));
  };

  const addIngredient = () => {
    setEditedDrink(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { name: '', amount: '' }]
    }));
  };

  const removeIngredient = (index) => {
    if (editedDrink.ingredients.length > 1) {
      const newIngredients = editedDrink.ingredients.filter((_, i) => i !== index);
      setEditedDrink(prev => ({
        ...prev,
        ingredients: newIngredients
      }));
    }
  };

  const handleInstructionChange = (index, value) => {
    const newInstructions = [...editedDrink.instructions];
    newInstructions[index] = value;
    setEditedDrink(prev => ({
      ...prev,
      instructions: newInstructions
    }));
  };

  const addInstruction = () => {
    setEditedDrink(prev => ({
      ...prev,
      instructions: [...prev.instructions, '']
    }));
  };

  const removeInstruction = (index) => {
    if (editedDrink.instructions.length > 1) {
      const newInstructions = editedDrink.instructions.filter((_, i) => i !== index);
      setEditedDrink(prev => ({
        ...prev,
        instructions: newInstructions
      }));
    }
  };

  const handleImageUpload = (imageUrl) => {
    setEditedDrink(prev => ({
      ...prev,
      image_url: imageUrl
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');

      const updatedDrink = {
        ...editedDrink,
        prep_time: editedDrink.prep_time ? parseInt(editedDrink.prep_time) : null,
        alcohol_content: editedDrink.alcohol_content ? parseFloat(editedDrink.alcohol_content) : null,
        ingredients: editedDrink.ingredients.filter(ing => ing.name.trim()),
        instructions: editedDrink.instructions.filter(inst => inst.trim()),
        tags: typeof editedDrink.tags === 'string' 
          ? editedDrink.tags.split(',').map(tag => tag.trim()).filter(Boolean)
          : editedDrink.tags || []
      };

      const savedDrink = await drinkAPI.update(drink.id, updatedDrink);
      onDrinkUpdated(savedDrink);
      setIsEditing(false);
    } catch (err) {
      setError('Failed to save drink. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditedDrink(drink);
    setIsEditing(false);
    setError('');
  };

  const formatTags = (tags) => {
    if (Array.isArray(tags)) {
      return tags.join(', ');
    }
    return tags || '';
  };

  const formatTime = (minutes) => {
    if (!minutes) return 'N/A';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  if (!isOpen) return null;

  return (
    <div className="drink-modal-overlay" onClick={onClose}>
      <div className="drink-modal-content" onClick={e => e.stopPropagation()} ref={swipeRef}>
        <div className="drink-modal-header">
          <h2>{isEditing ? 'Edit Drink' : editedDrink.title}</h2>
          <div className="drink-modal-actions">
            {canEdit && !isEditing && (
              <button 
                className="edit-button"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </button>
            )}
            <button className="close-button" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="drink-modal-body">
          {error && <div className="error-message">{error}</div>}

          <div className="drink-content">
            {/* Drink Image */}
            <div className="drink-image-section">
              {isEditing ? (
                <ImageUpload
                  currentImage={editedDrink.image_url}
                  onImageUpload={handleImageUpload}
                />
              ) : (
                editedDrink.image_url && (
                  <img 
                    src={editedDrink.image_url} 
                    alt={editedDrink.title}
                    className="drink-image"
                  />
                )
              )}
            </div>

            {/* Drink Info */}
            <div className="drink-info-section">
              {isEditing ? (
                <div className="edit-form">
                  <div className="form-group">
                    <label>Drink Title</label>
                    <input
                      type="text"
                      name="title"
                      value={editedDrink.title}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      name="description"
                      value={editedDrink.description || ''}
                      onChange={handleInputChange}
                      rows="3"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Prep Time (min)</label>
                      <input
                        type="number"
                        name="prep_time"
                        value={editedDrink.prep_time || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Glassware</label>
                      <input
                        type="text"
                        name="glassware"
                        value={editedDrink.glassware || ''}
                        onChange={handleInputChange}
                        placeholder="Highball, Martini, etc."
                      />
                    </div>
                    <div className="form-group">
                      <label>Garnish</label>
                      <input
                        type="text"
                        name="garnish"
                        value={editedDrink.garnish || ''}
                        onChange={handleInputChange}
                        placeholder="Lime wedge, Cherry, etc."
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="is_alcoholic"
                          checked={editedDrink.is_alcoholic || false}
                          onChange={handleInputChange}
                        />
                        <span>Contains Alcohol</span>
                      </label>
                    </div>
                    {editedDrink.is_alcoholic && (
                      <div className="form-group">
                        <label>Alcohol Content (%)</label>
                        <input
                          type="number"
                          name="alcohol_content"
                          value={editedDrink.alcohol_content || ''}
                          onChange={handleInputChange}
                          min="0"
                          max="100"
                          step="0.1"
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Tags (comma separated)</label>
                    <input
                      type="text"
                      name="tags"
                      value={formatTags(editedDrink.tags)}
                      onChange={handleInputChange}
                      placeholder="Cocktail, Rum, Refreshing"
                    />
                  </div>
                </div>
              ) : (
                <div className="drink-info">
                  {editedDrink.description && (
                    <div className="drink-description-container">
                      <p className={`drink-description ${showFullDescription ? 'expanded' : 'collapsed'}`}>
                        {editedDrink.description}
                      </p>
                      {editedDrink.description.length > 200 && (
                        <button 
                          className="toggle-description-button"
                          onClick={() => setShowFullDescription(!showFullDescription)}
                        >
                          {showFullDescription ? 'Show Less' : 'Show More'}
                        </button>
                      )}
                    </div>
                  )}
                  
                  <div className="drink-meta">
                    <div className="meta-item">
                      <span className="meta-label">Prep Time:</span>
                      <span className="meta-value">{formatTime(editedDrink.prep_time)}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Alcoholic:</span>
                      <span className={`meta-value ${editedDrink.is_alcoholic ? 'alcoholic' : 'non-alcoholic'}`}>
                        {editedDrink.is_alcoholic ? 'Yes' : 'No'}
                      </span>
                    </div>
                    {editedDrink.is_alcoholic && editedDrink.alcohol_content && (
                      <div className="meta-item">
                        <span className="meta-label">Alcohol Content:</span>
                        <span className="meta-value">{editedDrink.alcohol_content}%</span>
                      </div>
                    )}
                    {editedDrink.glassware && (
                      <div className="meta-item">
                        <span className="meta-label">Glassware:</span>
                        <span className="meta-value">{editedDrink.glassware}</span>
                      </div>
                    )}
                    {editedDrink.garnish && (
                      <div className="meta-item">
                        <span className="meta-label">Garnish:</span>
                        <span className="meta-value">{editedDrink.garnish}</span>
                      </div>
                    )}
                  </div>

                  {editedDrink.tags && editedDrink.tags.length > 0 && (
                    <div className="drink-tags">
                      {(Array.isArray(editedDrink.tags) ? editedDrink.tags : []).map((tag, index) => (
                        <span key={index} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Ingredients */}
          <div className="drink-section">
            <h3>Ingredients</h3>
            {isEditing ? (
              <div className="ingredients-edit">
                {editedDrink.ingredients.map((ingredient, index) => (
                  <div key={index} className="ingredient-row">
                    <input
                      type="text"
                      placeholder="Amount"
                      value={ingredient.amount}
                      onChange={(e) => handleIngredientChange(index, 'amount', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Ingredient"
                      value={ingredient.name}
                      onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => removeIngredient(index)}
                      className="remove-button"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addIngredient} className="add-button">
                  + Add Ingredient
                </button>
              </div>
            ) : (
              <ul className="ingredients-list">
                {editedDrink.ingredients.map((ingredient, index) => (
                  <li key={index} className="ingredient-item">
                    <span className="ingredient-amount">{ingredient.amount}</span>
                    <span className="ingredient-name">{ingredient.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Instructions */}
          <div className="drink-section">
            <h3>Instructions</h3>
            {isEditing ? (
              <div className="instructions-edit">
                {editedDrink.instructions.map((instruction, index) => (
                  <div key={index} className="instruction-row">
                    <span className="step-number">{index + 1}.</span>
                    <textarea
                      placeholder="Instruction step"
                      value={instruction}
                      onChange={(e) => handleInstructionChange(index, e.target.value)}
                      rows="2"
                    />
                    <button
                      type="button"
                      onClick={() => removeInstruction(index)}
                      className="remove-button"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addInstruction} className="add-button">
                  + Add Step
                </button>
              </div>
            ) : (
              <ol className="instructions-list">
                {editedDrink.instructions.map((instruction, index) => (
                  <li key={index} className="instruction-item">
                    {instruction}
                  </li>
                ))}
              </ol>
            )}
          </div>

          {editedDrink.source_url && (
            <div className="drink-source">
              <p>
                <strong>Source:</strong>{' '}
                <a href={editedDrink.source_url} target="_blank" rel="noopener noreferrer">
                  {editedDrink.source_url}
                </a>
              </p>
            </div>
          )}
        </div>

        {isEditing && (
          <div className="drink-modal-footer">
            <button 
              className="cancel-button" 
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              className="save-button" 
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DrinkDetailModal;